import toast from 'react-hot-toast';

import { api } from '@/utils/api';

export const useAiStepUpdateMutation = () => {
  const utils = api.useUtils();

  return api.aiStep.update.useMutation({
    onSuccess: async () => {
      // Updated step content is shown by the queue AND the fetchNext / all surfaces.
      await utils.aiStep.fetchNext.invalidate();
      await utils.aiStep.all.invalidate();
      await utils.aiStep.queue.invalidate();
    },
    onError: (error) => {
      toast.error(error.message || 'Failed to update message');
    },
  });
};

/**
 * Mutation hook for setting voice drop audio on an AI step
 *
 * NOTE: The `voiceDropAudioId` parameter is the `audio_file.id`, NOT `audio_drop_file.id`
 * This is confusing legacy naming - see AiStepService.setVoiceDrop for full explanation.
 */
export const useAiStepSetVoiceDropMutation = () => {
  const utils = api.useUtils();

  return api.aiStep.setVoiceDrop.useMutation({
    onSuccess: async () => {
      await utils.aiStep.fetchNext.invalidate();
      await utils.aiStep.all.invalidate();
    },
  });
};

export const useAiStepApproveMutation = () => {
  const utils = api.useUtils();

  return api.aiStep.approve.useMutation({
    onSuccess: async () => {
      await utils.aiStep.fetchNext.invalidate();
      await utils.aiStep.all.invalidate();
      await utils.aiStep.queue.invalidate();
      await utils.aiStep.queueCounts.invalidate();
      void utils.aiStep.countUnfinished.invalidate();
    },
  });
};

/**
 * Reject a contact by deleting all their AI steps and moving them to the rejected stage
 */
export const useAiStepRejectContactMutation = () => {
  const utils = api.useUtils();

  return api.aiStep.rejectContact.useMutation({
    onSuccess: async () => {
      // Deletes the contact's steps — the queue list + count badge must drop them.
      await utils.aiStep.fetchNext.invalidate();
      await utils.aiStep.all.invalidate();
      await utils.aiStep.queue.invalidate();
      await utils.aiStep.queueCounts.invalidate();
      void utils.aiStep.countUnfinished.invalidate();
    },
  });
};

export const useAiStepSkipMutation = () => {
  const utils = api.useUtils();

  return api.aiStep.skip.useMutation({
    onSuccess: async () => {
      // Skipping rejects the step — remove it from the queue list + count badge.
      await utils.aiStep.fetchNext.invalidate();
      await utils.aiStep.all.invalidate();
      await utils.aiStep.queue.invalidate();
      await utils.aiStep.queueCounts.invalidate();
      void utils.aiStep.countUnfinished.invalidate();
    },
  });
};

export const useAiStepRegenerateAudioMutation = () => {
  const utils = api.useUtils();

  return api.aiStep.regenerateAudio.useMutation({
    onSuccess: async () => {
      // The regenerated audio is cached on the step across all three surfaces.
      await utils.aiStep.fetchNext.invalidate();
      await utils.aiStep.all.invalidate();
      await utils.aiStep.queue.invalidate();
    },
  });
};

export const useAiStepBulkCreateMutation = () => {
  const utils = api.useUtils();

  return api.aiStep.bulkCreate.useMutation({
    onSuccess: () => {
      // New steps must appear in the queue list + count badge.
      void utils.aiStep.fetchNext.invalidate();
      void utils.aiStep.all.invalidate();
      void utils.aiStep.queue.invalidate();
      void utils.aiStep.queueCounts.invalidate();
      void utils.aiStep.countUnfinished.invalidate();
      void utils.aiStep.actionSectionData.invalidate();
    },
  });
};

export const useAiStepDeleteMutation = () => {
  const utils = api.useUtils();

  return api.aiStep.delete.useMutation({
    onSuccess: async () => {
      await utils.aiStep.fetchNext.invalidate();
      await utils.aiStep.all.invalidate();
      await utils.aiStep.queue.invalidate();
      await utils.aiStep.queueCounts.invalidate();
      void utils.aiStep.countUnfinished.invalidate();
    },
    onError: (error) => {
      toast.error(error.message || 'Failed to cancel step');
    },
  });
};

/** "Retry now" / "Resume AI drafts": enqueue an immediate drafts backfill for an account. */
export const useAiStepResumeDraftsMutation = () => api.aiStep.resumeDrafts.useMutation();

export const useAiStepGenerateContentMutation = () => {
  const utils = api.useUtils();

  return api.aiStep.generateContent.useMutation({
    onSuccess: async () => {
      // Generated body/subject is shown by the queue too.
      await utils.aiStep.fetchNext.invalidate();
      await utils.aiStep.all.invalidate();
      await utils.aiStep.queue.invalidate();
      void utils.aiStep.countUnfinished.invalidate();
    },
  });
};

export const useAiStepDeleteAllByStatusMutation = () => {
  const utils = api.useUtils();

  return api.aiStep.deleteAllByStatus.useMutation({
    onSuccess: async () => {
      // Bulk-deletes steps — the queue list + count badge must drop them.
      await utils.aiStep.fetchNext.invalidate();
      await utils.aiStep.all.invalidate();
      await utils.aiStep.queue.invalidate();
      await utils.aiStep.queueCounts.invalidate();
      void utils.aiStep.countUnfinished.invalidate();
    },
  });
};

export const useAiStepRestartMutation = () => {
  const utils = api.useUtils();

  return api.aiStep.restartStep.useMutation({
    onSuccess: async () => {
      await utils.aiStep.fetchNext.invalidate();
      await utils.aiStep.all.invalidate();
      await utils.aiStep.queue.invalidate();
      await utils.aiStep.queueCounts.invalidate();
      void utils.aiStep.countUnfinished.invalidate();
    },
    onError: (error) => {
      toast.error(error.message || 'Failed to retry step');
    },
  });
};

export const useAiStepBulkDeleteMutation = () => {
  const utils = api.useUtils();

  return api.aiStep.bulkDelete.useMutation({
    onSuccess: async () => {
      await utils.aiStep.fetchNext.invalidate();
      await utils.aiStep.all.invalidate();
      await utils.aiStep.queue.invalidate();
      await utils.aiStep.queueCounts.invalidate();
      void utils.aiStep.countUnfinished.invalidate();
    },
    onError: (error) => {
      toast.error(error.message || 'Failed to cancel selected steps');
    },
  });
};

export const useAiStepBulkRestartMutation = () => {
  const utils = api.useUtils();

  return api.aiStep.bulkRestart.useMutation({
    onSuccess: async () => {
      await utils.aiStep.fetchNext.invalidate();
      await utils.aiStep.all.invalidate();
      await utils.aiStep.queue.invalidate();
      await utils.aiStep.queueCounts.invalidate();
      void utils.aiStep.countUnfinished.invalidate();
    },
    onError: (error) => {
      toast.error(error.message || 'Failed to retry selected steps');
    },
  });
};

export const useAiStepTriggerQueueMutation = () => {
  const utils = api.useUtils();

  return api.aiStep.triggerQueueProcessing.useMutation({
    onSuccess: async (result) => {
      if (result.triggered) {
        toast.success(`Started processing ${result.queuedCount} queued ${result.queuedCount === 1 ? 'item' : 'items'}`);
      } else {
        toast(result.reason ?? 'Could not start processing');
      }
      await utils.aiStep.queueCounts.invalidate();
      await utils.aiStep.queue.invalidate();
    },
    onError: (error) => {
      toast.error(error.message || 'Failed to start queue processing');
    },
  });
};

export const useAiStepReportBadAudioMutation = () => {
  return api.aiStep.reportBadAudio.useMutation({
    onSuccess: () => {
      toast.success('Bad audio reported. Superadmins will review and request a refund.');
    },
    onError: (error) => {
      toast.error(error.message || 'Failed to report bad audio');
    },
  });
};
